import { useMutation, useQueryClient } from '@tanstack/react-query';
import { productKeys } from '@/services/queryKeys';
import { productService } from '@/services/product.service';
import type {
  CreateProductInput,
  UpdateProductInput,
  ProductState,
  VariantUpsertInput,
} from '@erpgold/shared';

/**
 * Variant whitelist khớp với VariantUpsertInput. BE từ chối field ngoài whitelist
 * (`forbidNonWhitelisted: true`). Loại các field server-managed như
 * is_price_paused/price_paused_*, cached_*, tenant_id, product_id, timestamps.
 */
const VARIANT_SUBMIT_KEYS: Array<keyof VariantUpsertInput> = [
  '_id',
  'variant_code',
  'is_default',
  'attrs',
  'pricing_model',
  'pricing_fixed',
  'pricing_buy_fixed',
  'pricing_ref',
  'fee_group_id',
  'fees',
  'is_sellable',
  'is_sellable_web',
  'is_sellable_pos',
  'is_sellable_app',
  'is_published',
  'image_intro',
];

function sanitizeVariant(v: Record<string, unknown>): VariantUpsertInput {
  const out: Record<string, unknown> = {};
  for (const key of VARIANT_SUBMIT_KEYS) {
    if (v[key] !== undefined) out[key] = v[key];
  }
  return out as unknown as VariantUpsertInput;
}

function sanitizeBody<T extends { variants?: unknown }>(body: T): T {
  if (!Array.isArray(body.variants)) return body;
  return {
    ...body,
    variants: (body.variants as Record<string, unknown>[]).map(sanitizeVariant),
  };
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductInput) => productService.create(sanitizeBody(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateProductInput }) =>
      productService.update(id, sanitizeBody(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useUpdateProductState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: ProductState }) =>
      productService.updateState(id, state),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useCloneProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productService.clone(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useBulkUpdateProductState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, state }: { ids: string[]; state: ProductState }) =>
      productService.bulkUpdateState(ids, state),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}
